"use client";

import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import Button from "../../components/Button/button";
import ChevronsRightIcon from "../../shared-components/icons/ChevronsRightIcon";
import { useUpsellSubscriptionContext } from "./upsell-subscription-context";
import { publishModalVisibility } from "../../redux/features/modal/modalSlice";
import { useAppDispatch } from "../../redux/hooks";
import { usePassWinners } from "@/app/hooks/usePassWinners";
import { convertStringToHTMLFormattedString } from "@/app/common-utils";
import "../../../embedViews/upsellModalStyles.css";

const BANNER_MOBILE = "/assets/images/botb-pass/banner-mobile-v2.webp";
const BANNER_DESKTOP = "/assets/images/botb-pass/banner-desktop-v2.webp";
const CLOSE_ICON = "/assets/images/upsell-subscription-modal/tabler-icon-x.svg";
const MAX_TESTIMONIALS = 5;

const MissOutContentPopup = (props?: any) => {
  const { onSecondTimeMayBeLater, setIsMaybeLaterOpen, setStep } = useUpsellSubscriptionContext();
  const { fromNavMenu = false, okClick } = props;
  const dispatch = useAppDispatch();
  const { winners, loading } = usePassWinners();

  const testimonials = useMemo(() => {
    return winners.reduce<
      {
        name: string;
        prize: string;
        quote: string;
        photo: string;
      }[]
    >((acc, winner) => {
      if (acc.length >= MAX_TESTIMONIALS) return acc;
      const quote = convertStringToHTMLFormattedString(winner.StoryPreview || "").trim();
      const photo = winner.CloseUpPhoto || winner.Photo;
      if (!quote || !photo) return acc;
      acc.push({
        name: winner.Name,
        prize: winner.Prize,
        quote,
        photo,
      });
      return acc;
    }, []);
  }, [winners]);

  const showTestimonialsSlot = loading || testimonials.length > 0;

  const isFromNavMenuClick = () => {
    if (fromNavMenu && okClick) {
      return (location.href = `${okClick}#main-plans-section`);
    } else {
      setIsMaybeLaterOpen(false);
      setStep(1);
    }
  };

  const isFromNavMenuClickClose = () => {
    if (fromNavMenu) {
      return dispatch(publishModalVisibility({ isOpen: false }));
    }
    onSecondTimeMayBeLater();
    return;
  };

  return (
    <div className="retention-modal relative flex flex-col items-center w-full bg-white rounded-lg lg:rounded-2xl">
      <div className="relative w-full shrink-0">
        <img
          src={BANNER_MOBILE}
          alt=""
          width={336}
          height={150}
          className="block lg:hidden w-full h-auto aspect-[336/150] object-cover"
        />
        <img
          src={BANNER_DESKTOP}
          alt=""
          width={494}
          height={220}
          className="hidden lg:block w-full h-auto aspect-[494/220] object-cover"
        />
        <button
          type="button"
          onClick={isFromNavMenuClickClose}
          aria-label="Close"
          className="absolute top-2 lg:top-3 right-2 lg:right-3 size-6">
          <img src={CLOSE_ICON} alt="" width={24} height={24} className="block size-6" />
        </button>
      </div>

      <div className="flex flex-col items-center gap-4 lg:gap-6 w-full pt-2 lg:pt-6 pb-6">
        <div className="flex flex-col items-center gap-3 lg:gap-4 w-full px-4 lg:px-8 text-center">
          <div className="max-w-[292px] lg:max-w-none text-xl lg:text-2xl font-semibold font-jost leading-[26px] lg:leading-[30px] text-primary normal-case tracking-[0] !mb-0">
            1 in 2 of our big winners this year are Pass members
          </div>
          <div className="w-full text-sm lg:text-base font-normal font-jost leading-5 lg:leading-[22px] text-secondary tracking-[0]">
            <span className="font-semibold text-dark-1">Don&apos;t miss out</span>
            . Pass from £9.99 a month.
            <br className="lg:hidden" /> Cancel anytime.
          </div>
        </div>

        {showTestimonialsSlot && (
          <div className="flex flex-col items-center gap-4 w-full min-w-0">
            <div className="w-full min-h-[88px] lg:min-h-[104px]">
              {testimonials.length > 0 ? (
                <Swiper
                  modules={[Pagination]}
                  slidesPerView={1.12}
                  spaceBetween={12}
                  slidesOffsetBefore={15}
                  slidesOffsetAfter={15}
                  pagination={{
                    clickable: true,
                    el: ".retention-modal-pagination",
                    bulletClass: "retention-modal-bullet",
                    bulletActiveClass: "retention-modal-bullet-active",
                  }}
                  breakpoints={{
                    1024: {
                      slidesPerView: 1.37,
                      spaceBetween: 16,
                      slidesOffsetBefore: 16,
                      slidesOffsetAfter: 16,
                    },
                  }}
                  className="w-full min-h-[88px] lg:min-h-[104px] !overflow-visible">
                  {testimonials.map((winner, index) => (
                    <SwiperSlide key={`${winner.name}-${index}`} className="!h-auto">
                      <div className="flex items-center gap-3 w-full min-h-[88px] lg:min-h-[104px] px-3 py-2 border border-solid border-black/[0.08] rounded-xl bg-white">
                        <img
                          src={winner.photo}
                          alt=""
                          className="size-10 shrink-0 rounded-full object-cover border-[3px] border-solid border-primary"
                        />
                        <div className="flex flex-col gap-2 min-w-0 flex-1">
                          <div className="text-xs lg:text-sm italic font-jost leading-4 lg:leading-5 text-gray-1 line-clamp-3 tracking-[0]">
                            {winner.quote}
                          </div>
                          <div className="flex items-center gap-1 lg:gap-2 min-w-0">
                            <span className="shrink-0 text-[11px] lg:text-[13px] font-bold font-jost leading-[14px] lg:leading-[18px] text-primary tracking-[0]">
                              {winner.name}
                            </span>
                            <span
                              className="size-1 lg:size-1.5 shrink-0 rounded-full bg-[#D9D9D9]"
                              aria-hidden="true"
                            />
                            <span className="min-w-0 truncate text-[11px] lg:text-[13px] font-semibold font-jost leading-[14px] lg:leading-[18px] text-dark-1 tracking-[0]">
                              {winner.prize}
                            </span>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <div
                  className="mx-[15px] lg:mx-4 min-h-[88px] lg:min-h-[104px] rounded-xl border border-solid border-black/[0.08] bg-gray-3"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="retention-modal-pagination min-h-2" />
          </div>
        )}

        <div className="w-full px-6 lg:flex lg:justify-center">
          <Button
            className="flex justify-center items-center gap-2 w-full lg:w-[340px] h-11 lg:h-12 text-white bg-primary hover:bg-[#FF6A00] rounded px-3 lg:px-6 font-jost text-base font-medium uppercase btnWithArrow"
            onClick={isFromNavMenuClick}
            text="">
            <span className="relative leading-[22px] lg:leading-6 flex items-center gap-1 font-jost text-base font-medium">
              SEE ALL PLANS
              <ChevronsRightIcon className="arrowImage size-5 lg:size-6 text-white" variant={3} />
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MissOutContentPopup;
